import React, { useEffect, useState, Component } from 'react';
import { Hero } from '../components/Hero';
import { ProblemSection } from '../components/ProblemSection';
import { CoverageSection } from '../components/CoverageSection';
import { HowItWorks } from '../components/HowItWorks';
import { SolutionSection } from '../components/SolutionSection';
import { AudienceSection } from '../components/AudienceSection';
import { FutureSection } from '../components/FutureSection';
import { TrustSection } from '../components/TrustSection';
import { CTASection } from '../components/CTASection';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { motion, useScroll, useSpring, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import { trackCTAClick } from '../utils/analytics';
// High-Quality Watchdog Logo Component
export const WatchdogLogo = ({
  className = 'w-8 h-8'
}: {
  className?: string;
}) => <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} data-id="element-375">
    <defs data-id="element-376">
      <linearGradient id="shieldGradient" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-377">
        <stop offset="0%" stopColor="#6366f1" data-id="element-378" />
        <stop offset="100%" stopColor="#4f46e5" data-id="element-379" />
      </linearGradient>
      <filter id="shadow" data-id="element-380">
        <feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity="0.2" data-id="element-381" />
      </filter>
    </defs>

    {/* Shield Background */}
    <path d="M24 4L8 10V20C8 30.5 14.5 40.5 24 43C33.5 40.5 40 30.5 40 20V10L24 4Z" fill="url(#shieldGradient)" filter="url(#shadow)" data-id="element-382" />

    {/* Inner Shield Highlight */}
    <path d="M24 6L10 11V20C10 29 15.5 37.5 24 40C32.5 37.5 38 29 38 20V11L24 6Z" fill="white" fillOpacity="0.1" data-id="element-383" />

    {/* Dog Eye (Left) */}
    <circle cx="19" cy="18" r="2.5" fill="white" data-id="element-384" />
    <circle cx="19.5" cy="17.5" r="1.2" fill="#1e293b" data-id="element-385" />

    {/* Dog Eye (Right) */}
    <circle cx="29" cy="18" r="2.5" fill="white" data-id="element-386" />
    <circle cx="29.5" cy="17.5" r="1.2" fill="#1e293b" data-id="element-387" />

    {/* Dog Nose */}
    <path d="M24 22C22.5 22 21.5 23 21.5 24C21.5 25 22.5 26 24 26C25.5 26 26.5 25 26.5 24C26.5 23 25.5 22 24 22Z" fill="white" data-id="element-388" />

    {/* Dog Mouth */}
    <path d="M24 26C24 26 21 28 18 27M24 26C24 26 27 28 30 27" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.8" data-id="element-389" />

    {/* Alert Badge */}
    <circle cx="36" cy="12" r="5" fill="#f59e0b" data-id="element-390" />
    <path d="M36 10V13M36 15V15.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" data-id="element-391" />
  </svg>;
// Watchdog Text Logo Component (for larger displays)
export const WatchdogTextLogo = ({
  className = ''
}: {
  className?: string;
}) => <div className={`flex items-center gap-3 ${className}`} data-id="element-392">
    <WatchdogLogo className="w-10 h-10" data-id="element-393" />
    <div className="flex flex-col" data-id="element-394">
      <span className="text-2xl font-bold text-slate-900 tracking-tight leading-none" data-id="element-395">
        Watchdog
      </span>
      <span className="text-xs text-slate-500 font-medium tracking-wide" data-id="element-396">
        TRADEMARK PROTECTION
      </span>
    </div>
  </div>;
export function LandingPage() {
  const {
    scrollYProgress
  } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-indigo-100 selection:text-indigo-900 overflow-x-hidden" data-id="element-397">
      {/* Scroll Progress Bar */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-indigo-600 origin-left z-[100]" style={{
      scaleX
    }} data-id="element-398" />

      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled || isMobileMenuOpen ? 'bg-white/90 backdrop-blur-xl border-b border-slate-200/50 shadow-sm' : 'bg-transparent'}`} data-id="element-399">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-400">
          <div className="flex justify-between h-16 items-center" data-id="element-401">
            {/* Logo */}
            <motion.div whileHover={{
            scale: 1.02
          }} className="flex items-center space-x-2 cursor-pointer group z-50" data-id="element-402">
              <WatchdogLogo className="w-9 h-9 group-hover:scale-110 transition-transform duration-300" data-id="element-403" />
              <span className="text-xl font-bold text-slate-900 tracking-tight" data-id="element-404">
                Watchdog
              </span>
            </motion.div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-8" data-id="element-405">
              <a href="https://app.watchdog.buzz/" target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('nav')} data-id="element-406">
                <Button variant="primary" size="md" icon={<ArrowRight className="w-4 h-4" data-id="element-408" />} data-id="element-407">
                  Start Free Trial
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center z-50" data-id="element-409">
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 text-slate-600 hover:text-indigo-600 transition-colors" aria-label="Toggle menu" data-id="element-410">
                {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-411" /> : <Menu className="w-6 h-6" data-id="element-412" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence data-id="element-413">
          {isMobileMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="md:hidden bg-white border-b border-slate-200 overflow-hidden" data-id="element-414">
              <div className="px-4 pt-4 pb-8 space-y-4 flex flex-col" data-id="element-415">
                <div className="pt-4" data-id="element-416">
                  <a href="https://app.watchdog.buzz/" target="_blank" rel="noopener noreferrer" className="block" onClick={() => trackCTAClick('mobile_nav')} data-id="element-417">
                    <Button variant="primary" size="lg" className="w-full justify-center" icon={<ArrowRight className="w-4 h-4" data-id="element-419" />} data-id="element-418">
                      Start Free Trial
                    </Button>
                  </a>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>

      <main data-id="element-420">
        <Hero data-id="element-421" />
        <CoverageSection data-id="element-422" />
        <ProblemSection data-id="element-423" />
        <HowItWorks data-id="element-424" />
        <SolutionSection data-id="element-425" />
        <TrustSection data-id="element-426" />
        <AudienceSection data-id="element-427" />
        <FutureSection data-id="element-428" />
        <CTASection data-id="element-429" />
      </main>

      <Footer data-id="element-430" />
    </div>;
}